import type { ReactNode } from 'react';
import styled from 'styled-components';
import { PlayIcon } from '@phosphor-icons/react';
import { AlertCard } from '../../../AlertCard';
import { Spinner } from '../../../Spinner';
import { colors, radii, spacing, typography } from '../../../../theme-v2';
import { AddonLayout } from '../AddonLayout';

export interface VideoPosterCardProps {
  /** Poster image shown behind the play button. */
  poster?: string;
  /** Optional caption shown above the card (consumer-translated). */
  title?: ReactNode;
  /**
   * Fires when the user clicks play. The experience page expands the
   * full chaptered player over the sidebar region in response. Receives
   * the poster's on-screen rect so the expand can grow out of the
   * thumbnail (FLIP animation in ExperiencePageLayout).
   */
  onPlay?: (origin: DOMRect) => void;
  /** Accessible label for the play button (consumer-translated). */
  playLabel?: string;
  /** Loading state — shows a spinner instead of the card. */
  loading?: boolean;
  /** Error message — shows an error card instead of the poster. */
  error?: ReactNode;
  /** Error-card title (consumer-translated). Shown on error / no video. */
  errorTitle?: string;
  className?: string;
}

/**
 * VideoPosterCard — collapsed Video add-on shown inside the Knowledge
 * Hub tab. Displays the video poster with a play button; clicking it
 * calls `onPlay`, which the experience page uses to expand the full
 * `ChapteredVideoPlayer` over the sidebar region (the document stays in
 * place on the left).
 *
 * Stateless. Apps own the data (poster/url from the publication's media)
 * and the expand state; this component owns the collapsed framing.
 */
export const VideoPosterCard = ({
  poster,
  title,
  onPlay,
  playLabel,
  loading,
  error,
  errorTitle,
  className,
}: VideoPosterCardProps) => {
  let body: ReactNode;
  if (loading) {
    body = (
      <StyledCentered>
        <Spinner />
      </StyledCentered>
    );
  } else if (error) {
    body = (
      <StyledCentered>
        <AlertCard
          type="error"
          title={errorTitle ?? ''}
          description={typeof error === 'string' ? error : undefined}
        />
      </StyledCentered>
    );
  } else {
    body = (
      <StyledPoster
        type="button"
        aria-label={playLabel}
        onClick={(e) => onPlay?.(e.currentTarget.getBoundingClientRect())}
        $poster={poster}
      >
        <StyledPlayButton>
          <PlayIcon size={32} weight="fill" />
        </StyledPlayButton>
      </StyledPoster>
    );
  }

  return (
    <StyledWrapper className={className}>
      {title && <StyledTitle>{title}</StyledTitle>}
      <StyledStage>{body}</StyledStage>
    </StyledWrapper>
  );
};

const StyledWrapper = styled(AddonLayout)`
  gap: ${spacing.sm}px;
`;

const StyledTitle = styled.h3`
  margin: 0;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.lineHeight.tight};
  color: ${colors.text.primary};
`;

const StyledStage = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  background: #0f172a;
  border-radius: ${radii.box}px;
  overflow: hidden;
`;

const StyledPoster = styled.button<{ $poster?: string }>`
  position: relative;
  flex: 1;
  width: 100%;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0f172a;
  background-image: ${({ $poster }) => ($poster ? `url(${$poster})` : 'none')};
  background-size: cover;
  background-position: center;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.35);
    transition: background 0.2s ease;
  }

  &:hover::after,
  &:focus-visible::after {
    background: rgba(15, 23, 42, 0.2);
  }

  &:focus-visible {
    outline: 2px solid ${colors.accent.base};
    outline-offset: -2px;
  }
`;

const StyledPlayButton = styled.span`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: ${radii.full}px;
  color: #fff;
  background: rgba(255, 255, 255, 0.18);
  border: 2px solid rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(8px);
  transition:
    transform 0.2s ease,
    background 0.2s ease;

  /* Nudge the triangle to optical center. */
  svg {
    margin-left: 4px;
  }

  ${StyledPoster}:hover &,
  ${StyledPoster}:focus-visible & {
    transform: scale(1.06);
    background: rgba(255, 255, 255, 0.28);
  }
`;

const StyledCentered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${spacing.lg}px;
`;
